import {
	Alert,
	AlertIcon,
	Button,
	HStack,
	Progress,
	Stack,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { Gender, ProLeadSource, ReasonForJoiningLaborHack } from "src/types";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { IoChevronBackOutline } from "react-icons/io5";
import { FiArrowRight } from "react-icons/fi";
import PersonalInformationForm from "./PersonalInformationForm";
import { RequestFailed } from "./RequestFailed";
import { Loading } from "./Loading";
import ContactAndProfessionalInformationForm from "./ContactAndProfessionalInformation";
import OtherInformation from "./OtherInformation";
import { useMutation, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { SUBMIT_APPLICATION } from "src/pages/apply/constants";
import { isDate } from "date-fns";
import { useProfile } from "src/hooks/useProfile";
import { RegistrationConfirmation } from "./ConfirmationModals";
import { RegistrationSteps } from "../features/registration/types";
import { ExistingPhoneNumber } from "../features/registration/existing-phone-number";
import {
	JOB_TAGS,
	MY_JOB_APPLICANT_PROFILE,
} from "src/common/graphql/typed-queries";
import countries from "../assets/countries.json";
import currencies from "../assets/currencies.json";
import { logEvent } from "src/utlis/analytics";
import { useLocation } from "react-router-dom";
import {
	EmploymentStatus,
	PhoneType,
} from "src/__generated__/graphql";

export default function ApplicationFormV2() {
	const {
		profile,
		loading: loadingProfile,
		error: profileError,
	} = useProfile();

	const getJobTagsHandle = useQuery(JOB_TAGS);

	const jobApplicantQuery = useQuery(MY_JOB_APPLICANT_PROFILE);

	const { user, getAccessTokenSilently } = useAuth0();

	const { search } = useLocation();

	const searchParams = new URLSearchParams(search);

	const channel = searchParams.get("channel");

	/**
	 * Get referredBy from local storage
	 */
	const referredByFromLocalStorage = localStorage.getItem("referredBy");

	/**
	 * Check if lead source exists in local storage
	 */
	const leadSourceFromLocalStorage = localStorage.getItem("leadSource");

	/**
	 * Check if local storage lead source is a valid lead source
	 */
	const localStorageLeadSourceIsValid =
		leadSourceFromLocalStorage &&
		Object.values(ProLeadSource).includes(
			leadSourceFromLocalStorage as ProLeadSource
		);

	const redirectTo = localStorage.getItem("redirect");

	const tags = getJobTagsHandle.data?.jobAdTags ?? [];

	const [submitApplication, submitApplicationResponse] = useMutation(
		SUBMIT_APPLICATION,
		{
			onCompleted: () => {
				if (user) {
					user.given_name = step2formData.firstName;
					user.family_name = step2formData.lastName;
				}

				localStorage.removeItem("leadSource");

				localStorage.removeItem("referredBy");

				getAccessTokenSilently()
					.then(() => {
						console.log("Token refreshed");
						logEvent("pro/app/application_submitted");
						setRegistrationConfirmationIsOpen(true);
					})
					.catch((error) => {
						/**
						 * Fallback to redirecting without token
						 *
						 * The browser reloads the page and the user is redirected to the login page
						 * which would go through the authentication process again
						 */
						console.log(error);
						window.open("/login", "_self");
					});
			},
			refetchQueries: ["GetMyProfile"],
			awaitRefetchQueries: true,
		}
	);

	const [step, setStep] = React.useState<RegistrationSteps>(1);
	const [registrationConfirmationIsOpen, setRegistrationConfirmationIsOpen] =
		React.useState(false);

	const [step1IsValid, setStep1IsValid] = React.useState(false);

	const step2Schema = z
		.object({
			firstName: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string()
			),
			lastName: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string()
			),
			dateOfBirth: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string()
			),
			gender: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.nativeEnum(Gender)
			),
			jobTitle: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string().nonempty("Profession is required")
			),
			tags: z.array(z.string()).default([]),
			userAddedTags: z.array(z.string()).default([]),
		})
		.refine(
			(data) => {
				if (data.firstName && data.firstName.includes(" ")) {
					return false;
				}

				return true;
			},
			{ message: "First name cannot contain spaces", path: ["firstName"] }
		)
		.refine(
			(data) => {
				if (data.lastName && data.lastName.includes(" ")) {
					return false;
				}

				return true;
			},
			{ message: "Last name cannot contain spaces", path: ["lastName"] }
		)
		.refine(
			(data) => {
				if (data.tags.length > 0) {
					return true;
				}

				if (data.userAddedTags.length > 0) {
					return true;
				}

				return false;
			},
			{
				message: "At least one tag is required",
				path: ["tags"],
			}
		);
	const {
		control: controlStep2,
		register: registerStep2,
		watch: watchStep2,
		setValue: setStep2Value,
		formState: { touchedFields: step2TouchedFields },
	} = useForm<z.infer<typeof step2Schema>>({
		resolver: zodResolver(step2Schema),
		defaultValues: {
			tags: [],
			userAddedTags: [],
		},
	});

	const step3Schema = z
		.object({
			country: z.object({
				label: z.string(),
				value: z.string(),
			}),
			phoneNumber: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string()
			),
			detailedAddress: z.object({
				placeId: z.string(),
				address: z.string(),
				formattedAddress: z.string(),
				state: z.string().optional(),
				city: z.string().optional(),
			}),
			employmentStatus: z.nativeEnum(EmploymentStatus),
			incomeCurrency: z.object({
				label: z.string(),
				value: z.string(),
			}),
			averageMonthlyIncome: z.preprocess(
				(val) => (val === 0 ? undefined : val),
				z.number().optional()
			),
			yearsOfExperience: z
				.preprocess((val) => Number(val), z.number())
				.optional(),
			noExperience: z.boolean(),
		})
		.refine(
			(data) => {
				// If unemployed, no income required
				if (data.employmentStatus === EmploymentStatus.Unemployed) {
					return true;
				}
				// Otherwise, income is required
				return data.averageMonthlyIncome !== undefined && data.averageMonthlyIncome > 0;
			},
			{
				message: "Average monthly income is required",
				path: ["averageMonthlyIncome"],
			}
		)
		.refine(
			(data) => {
				if (
					!data.noExperience &&
					data.yearsOfExperience !== undefined &&
					data.yearsOfExperience < 1
				) {
					return false;
				}

				return true;
			},
			{
				message: "Years of experience is required",
				path: ["yearsOfExperience"],
			}
		);

	React.useEffect(() => {
		if (profile) {
			setStep2Value("firstName", profile.firstName);
			setStep2Value("lastName", profile.lastName);
		}
	}, [profile, setStep2Value]);

	const {
		control: controlStep3,
		register: registerStep3,
		watch: watchStep3,
		setValue: setStep3Value,
		formState: { touchedFields: step3TouchedFields },
	} = useForm<z.infer<typeof step3Schema>>({
		resolver: zodResolver(step3Schema),
	});

	useEffect(() => {
		if (profile && profile.phoneNumber && profile.phoneNumberVerified) {
			setStep3Value("phoneNumber", profile.phoneNumber);
			setStep(2);
		}

		const jobApplicantProfile = jobApplicantQuery.data?.myJobApplicantProfile;

		if (jobApplicantProfile) {
			if (jobApplicantProfile.phoneNumber) {
				setStep3Value("phoneNumber", jobApplicantProfile.phoneNumber);
				setStep(2);
			}

			setStep2Value("firstName", jobApplicantProfile.firstName);

			setStep2Value("lastName", jobApplicantProfile.lastName);

			if (
				jobApplicantProfile.yearsOfExperience !== undefined &&
				jobApplicantProfile.yearsOfExperience !== null
			) {
				setStep3Value(
					"yearsOfExperience",
					jobApplicantProfile.yearsOfExperience
				);
			}
		}
	}, [profile, setStep3Value, setStep, setStep2Value, jobApplicantQuery.data]);

	const step4Schema = z
		.object({
			noOfDaysOfWorkPerWeek: z.preprocess(
				(val) => (val === "" ? undefined : Number(val)),
				z.number()
			),
			phoneType: z.nativeEnum(PhoneType),
			leadSource: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.nativeEnum(ProLeadSource)
			),
			otherLeadSource: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string().optional()
			),
			reasonForJoiningLaborHack: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.nativeEnum(ReasonForJoiningLaborHack)
			),
			reasonForJoiningLaborHackOtherText: z.preprocess(
				(val) => (val === "" ? undefined : val),
				z.string().optional()
			),
		})
		.refine((data) => {
			if (data.reasonForJoiningLaborHack === ReasonForJoiningLaborHack.OTHER) {
				return !!data.reasonForJoiningLaborHackOtherText;
			}

			return true;
		})
		.refine((data) => {
			if (data.leadSource === ProLeadSource.OTHER) {
				return !!data.otherLeadSource;
			}

			return true;
		});

	const {
		register: registerStep4,
		watch: watchStep4,
		setValue: setStep4Value,
		formState: { touchedFields: step4TouchedFields },
	} = useForm<z.infer<typeof step4Schema>>({
		resolver: zodResolver(step4Schema),
	});

	React.useEffect(() => {
		if (leadSourceFromLocalStorage && localStorageLeadSourceIsValid) {
			setStep4Value("leadSource", leadSourceFromLocalStorage as ProLeadSource);
		}
	}, [
		leadSourceFromLocalStorage,
		localStorageLeadSourceIsValid,
		setStep4Value,
	]);

	const goBack = () => {
		if (step === 1) {
			return;
		}

		const newStep = (step - 1) as RegistrationSteps;

		setStep(newStep);
	};

	const step2formData = watchStep2();
	const step3formData = watchStep3();
	const step4formData = watchStep4();
	const step2IsValid = step2Schema.safeParse(step2formData).success;
	const step3IsValid = step3Schema.safeParse(step3formData).success;
	const step4IsValid = step4Schema.safeParse(step4formData).success;

	const stepsValidityMap = {
		1: step1IsValid,
		2: step2IsValid,
		3: step3IsValid,
		4: step4IsValid,
	};

	const canProceed = stepsValidityMap[step];

	const handleProceed = () => {
		const newStep = (step + 1) as 1 | 2 | 3;
		const currentStepName =
			step === 1
				? "pro/app/existing_phone_number"
				: step === 2
				? "pro/app/personal_information"
				: step === 3
				? "pro/app/contact_and_professional_information"
				: "pro/app/other_information";

		logEvent(`${currentStepName}_step_proceeded`);

		setStep(newStep);
	};

	const dateOfBirth = step2formData.dateOfBirth;

	const isValidDateOfBirth = dateOfBirth && isDate(new Date(dateOfBirth));

	const countryName =
		countries[step3formData?.country?.value as keyof typeof countries]?.name;
	const incomeCurrencyName =
		currencies[step3formData?.incomeCurrency?.value as keyof typeof currencies]
			?.name;

	const tagsInput: string[] = step2formData?.tags ?? [];

	const input = {
		firstName: step2formData?.firstName,
		lastName: step2formData?.lastName,
		email: user?.email,
		phoneNumber: step3formData?.phoneNumber,
		dateOfBirth: isValidDateOfBirth
			? new Date(dateOfBirth).toISOString()
			: undefined,
		gender: step2formData?.gender,
		jobTitle: step2formData?.jobTitle,
		tags: tagsInput,
		...(step2formData?.userAddedTags?.length
			? { userAddedTags: step2formData.userAddedTags }
			: {}),
		country: countryName,
		detailedAddress: step3formData?.detailedAddress,
		leadSource: step4formData?.leadSource,
		...(step4formData?.leadSource === ProLeadSource.OTHER && {
			leadSourceText: step4formData?.otherLeadSource,
		}),
		reasonForJoiningLaborHack: step4formData?.reasonForJoiningLaborHack,
		...(step4formData?.reasonForJoiningLaborHack ===
			ReasonForJoiningLaborHack.OTHER && {
			reasonForJoiningLaborHackOtherText:
				step4formData?.reasonForJoiningLaborHackOtherText,
		}),
		employmentStatus: step3formData?.employmentStatus,
		averageMonthlyIncome:
			step3formData?.employmentStatus === EmploymentStatus.Unemployed
				? 0
				: step3formData?.averageMonthlyIncome,
		incomeCurrency: incomeCurrencyName,
		yearsOfExperience: step3formData?.noExperience
			? 0
			: Number(step3formData?.yearsOfExperience),
		numberOfDaysWorkedPerWeek: Number(step4formData?.noOfDaysOfWorkPerWeek),
		phoneType: step4formData?.phoneType,
		referredBy: referredByFromLocalStorage,
		channel,
	};

	const handleSubmit = () => {
		submitApplication({ variables: { input } });
	};

	if (loadingProfile || getJobTagsHandle.loading) {
		return <Loading />;
	}

	if (profileError) {
		return <RequestFailed text="Failed to load profile" />;
	}

	if (getJobTagsHandle.error) {
		return <RequestFailed text="Failed to load job tags" />;
	}

	if (submitApplicationResponse.error) {
		return (
			<RequestFailed
				text={submitApplicationResponse.error.message}
				onRetry={submitApplicationResponse.reset}
			/>
		);
	}

	return (
		<>
			<Helmet>
				<title>Application Form</title>
			</Helmet>
			<Stack
				minH={"90vh"}
				my={{
					base: -3,
					lg: -10,
				}}
				mx={{
					base: -3,
					lg: -14,
				}}
			>
				<Stack
					display={"flex"}
					flexGrow={1}
					flexDirection={"column"}
					spacing={0}
					w={"full"}
				>
					<HStack
						borderY={"1px"}
						borderColor={"#E7E7E7"}
						py={{
							base: 4,
							lg: 6,
						}}
						textColor={"#8F8F8F"}
						px={{
							base: 3,
							lg: 12,
						}}
					>
						<div
							className="hover:cursor-pointer flex items-center gap-3"
							onClick={goBack}
						>
							<IoChevronBackOutline />
							<span className="text-lg lg:text-xl">Back</span>
						</div>
					</HStack>
					{redirectTo && redirectTo.startsWith("/my-job-applications") && (
						<div className="w-full">
							<Alert
								status="info"
								w={"full"}
								px={{
									base: 4,
									lg: 12,
								}}
							>
								<AlertIcon />
								<p>
									Complete the registration process to continue with your job
									application
								</p>
							</Alert>
						</div>
					)}

					<HStack
						borderBottom={"1px"}
						borderColor={"#E7E7E7"}
						py={6}
						px={{
							base: 4,
							lg: 12,
						}}
						w={"full"}
						justifyContent={"space-between"}
					>
						<Stack w={"60%"}>
							<p className="text-base lg:text-xl text-[#828282]">
								Step <span className="font-medium text-[#212121]">{step}</span>{" "}
								of <span className="font-medium text-[#212121]">4</span>
							</p>
							<Progress
								value={step === 4 ? 100 : step * 25}
								background={"#EEEEEE"}
								colorScheme="brand"
								rounded={"lg"}
							/>
						</Stack>
					</HStack>

					<div className="mb-auto w-full px-4 lg:px-12 border-b-[1px] border-[#E7E7E7]">
						{step === 1 && (
							<ExistingPhoneNumber
								onProceed={handleProceed}
								setIsValid={(valid: boolean) => {
									setStep1IsValid(valid);
								}}
								initialPhoneNumber={step3formData.phoneNumber}
								addPhoneNumber={(phoneNumber) => {
									setStep3Value("phoneNumber", phoneNumber);
								}}
							/>
						)}
						{step === 2 && (
							<PersonalInformationForm
								tags={tags}
								control={controlStep2}
								registerPersonalInfo={registerStep2}
								touchedFields={step2TouchedFields}
								formData={step2formData}
							/>
						)}
						{step === 3 && (
							<ContactAndProfessionalInformationForm
								control={controlStep3}
								registerContactAndProfessionalInfo={registerStep3}
								setValue={setStep3Value}
								touchedFields={step3TouchedFields}
								formData={step3formData}
							/>
						)}
						{step === 4 && (
							<OtherInformation
								registerOtherInfo={registerStep4}
								touchedFields={step4TouchedFields}
								formData={step4formData}
							/>
						)}
					</div>

					<HStack
						py={6}
						px={{
							base: 4,
							lg: 12,
						}}
						w={"full"}
						justifyContent={"space-between"}
					>
						<Stack mt={"auto"} ml={"auto"}>
							<Button
								px={{
									base: "20px",
									lg: "30px",
								}}
								py={{
									base: "20px",
									lg: "26px",
								}}
								fontSize={"20px"}
								fontWeight={500}
								colorScheme="brand"
								rounded={0}
								rightIcon={<FiArrowRight size={"18px"} />}
								isDisabled={!canProceed}
								isLoading={submitApplicationResponse.loading}
								onClick={step === 4 ? handleSubmit : handleProceed}
							>
								{step === 4 ? "Submit" : "Proceed"}
							</Button>
						</Stack>
					</HStack>
				</Stack>
			</Stack>
			{registrationConfirmationIsOpen && (
				<RegistrationConfirmation
					navigateTo={redirectTo ? redirectTo : undefined}
				/>
			)}
		</>
	);
}
